import { FC } from 'react'

export interface SentimentDonutBand {
  color: string
  value: number
}

interface SentimentDonutProps {
  // In the order they run around the ring, starting at twelve o'clock. They
  // are painted back to front, so where two arcs meet the later one's rounded
  // cap covers the earlier one's and the ring reads as continuous.
  bands: SentimentDonutBand[]
  // The square the ring is drawn in. The stroke follows it unless a caller
  // needs a different weight; 14 on 100 is the ratio the rest of Review
  // Analysis uses.
  size?: number
  strokeWidth?: number
  // What shows through where there is nothing to draw — a facility with no
  // reviews is an empty ring rather than no ring.
  trackColor?: string
}

const DEFAULT_SIZE = 100
const STROKE_RATIO = 0.14

/*
 * The sentiment ring Review Analysis draws everywhere: three arcs of one
 * circle, rounded at the ends, layered so they meet without a notch.
 *
 * Deliberately SVG rather than a chart library. ECharts rounds each slice as
 * its own shape, which leaves a gap at every join and turns a two-percent band
 * into a floating lozenge — the ring stops being one ring.
 */
export const SentimentDonut: FC<SentimentDonutProps> = ({
  bands,
  size = DEFAULT_SIZE,
  strokeWidth = Math.round(size * STROKE_RATIO),
  trackColor = '#e8e8f0',
}) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const total = bands.reduce((sum, band) => sum + band.value, 0) || 1

  // Where each arc starts, in the order the caller gave them.
  let consumed = 0
  const arcs = bands.map((band) => {
    const length = (band.value / total) * circumference
    const offset = consumed

    consumed += length

    return { ...band, length, offset }
  })

  return (
    <svg
      height={size}
      // From twelve o'clock rather than three, which is where a reader starts.
      style={{ transform: 'rotate(-90deg)' }}
      viewBox={`0 0 ${size} ${size}`}
      width={size}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        fill="none"
        r={radius}
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />

      {/* Reversed: the first band ends up on top, so it is the one whose
          rounded cap is whole where it meets its neighbour. */}
      {[...arcs]
        .reverse()
        // A band worth nothing has no arc. Drawn anyway, a zero-length stroke
        // with a round cap is a dot on the ring.
        .filter((arc) => arc.length > 0)
        .map((arc) => (
          <circle
            cx={size / 2}
            cy={size / 2}
            fill="none"
            key={arc.color}
            r={radius}
            stroke={arc.color}
            strokeDasharray={`${arc.length} ${circumference - arc.length}`}
            strokeDashoffset={-arc.offset}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        ))}
    </svg>
  )
}
